/**
 * THE OVERVIEW LOADER'S SCENE (Hamzah, 2026-10-20: "use this one", the "Overview loader" section of
 * the loader artifact, ported as approved). A bank site's plant in the Digital Twin's isometric
 * style — the grid's pylon, a transformer, a UPS and a row of racks — set on a ring around a
 * floating Thunder screen. Power runs round the ring once and closes it; only then does Thunder
 * switch on. After that it keeps flowing: every reading runs in along a spoke to Thunder's hub and
 * up onto its site-load chart. It loops with no restart for as long as the load takes, and when the
 * data is in it resolves (`RESOLVE_MS`) into the cards.
 *
 * NOTHING ON IT IS A READING THE SITE DID NOT SEND. The artifact carried example figures — a last
 * load of 182 kW at 10:42, a faded "last stored" chart, "30 racks" — and a loader that shows them
 * shows numbers no meter reported. Here the screen names the site the header has selected, its
 * chart is motion and nothing else (no axis, no figure), and a load appears only once the site's
 * own reading is in, during the resolve, as the Overview's own site load (`SceneLabel.kw`).
 *
 * Drawn on a canvas, 330 × 190 units, by `drawScene` — called every frame by `RingLoader.tsx`,
 * which owns the frame loop, the theme and reduced motion. Everything here is a function of the
 * time and the palette, so a frame can be drawn for any moment; the helpers are pure and tested.
 */

export const CW = 330;
export const CH = 190;

/* ── Timeline ─────────────────────────────────────────────────────────────────────────────────
   Intro (once): the pulse reaches the grid at 0 s, the transformer 0.6 s, the UPS 0.97 s, the
   racks 1.32 s; it closes the ring at 2.0 s and Thunder powers on. Steady (for ever): current
   flows on every line, the racks scan, a reading flies in every 0.62 s. No fade and no reset. */
export const ARR = [0, 0.6, 0.97, 1.32] as const;
export const INTRO = 2.4;
export const LOOP = [1.36, 2.0] as const;
export const EMIT0 = 2.55;
export const EMIT_EVERY = 0.62;
export const FLY = 0.6;
/** Which piece each reading leaves from: 0 grid, 1 transformer, 2 UPS, 3 racks. */
export const SEQ = [0, 1, 2, 3, 3, 2, 1, 3] as const;
/** From the data arriving to the cards: the screen flashes, the reading lands, the loader goes. */
export const RESOLVE_MS = 600;
/** The still frame drawn under reduced motion: everything powered, nothing moving. */
export const STILL_T = 4.4;

const STEADY_STEPS = ['three-phase power', 'site load', 'rack row', 'UPS', 'grid feed', 'transformer'] as const;

export const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));
export const smooth = (a: number, b: number, v: number): number => {
  const x = clamp01((v - a) / (b - a));
  return x * x * (3 - 2 * x);
};
export const easeIO = (v: number): number => {
  const x = clamp01(v);
  return x < 0.5 ? 2 * x * x : 1 - (-2 * x + 2) ** 2 / 2;
};
/** A repeatable pseudo-random 0–1 per index: the racks' lights twinkle the same way every time. */
export const seeded = (i: number): number => {
  const s = Math.sin(i * 12.9898 + 78.233) * 43758.5453;
  return s - Math.floor(s);
};

/** What the status line under the scene says it is reading at time `t`. */
export function stepFor(t: number): string {
  if (t < ARR[1]) return 'grid feed';
  if (t < ARR[2]) return 'transformer';
  if (t < ARR[3]) return 'UPS';
  if (t < LOOP[1]) return 'rack row';
  if (t < INTRO + 0.5) return 'Thunder';
  return STEADY_STEPS[Math.floor((t - INTRO - 0.5) / 1.6) % STEADY_STEPS.length] as string;
}

/**
 * The readings in flight at `t`, the last three: when each left, which piece it left from, and its
 * running number. None leaves after the data arrived (`tDone`); those already flying land.
 */
export function emitsAt(t: number, tDone: number): Array<{ at: number; src: number; n: number }> {
  const nNow = Math.floor((t - EMIT0) / EMIT_EVERY);
  const out: Array<{ at: number; src: number; n: number }> = [];
  for (let n = Math.max(0, nNow - 2); n <= nNow; n++) {
    const at = EMIT0 + n * EMIT_EVERY;
    if (at > tDone) continue;
    out.push({ at, src: SEQ[n % SEQ.length] as number, n });
  }
  return out;
}

/** `text` cut to fit `maxW` as measured, with an ellipsis; whole when it fits. */
export function truncateToWidth(text: string, maxW: number, measure: (s: string) => number): string {
  if (measure(text) <= maxW) return text;
  for (let n = text.length - 1; n > 0; n--) {
    const s = `${text.slice(0, n).trimEnd()}…`;
    if (measure(s) <= maxW) return s;
  }
  return '';
}

/**
 * A colour as `#rrggbb`, from what a canvas normalises a CSS colour to (`#rrggbb`, or `rgb(…)` /
 * `rgba(…)` for anything else). Null when it is neither — the caller keeps its fallback.
 */
export function hexOf(value: string): string | null {
  const v = value.trim().toLowerCase();
  if (/^#[0-9a-f]{6}$/.test(v)) return v;
  if (/^#[0-9a-f]{3}$/.test(v)) return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`;
  const m = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/.exec(v);
  if (!m) return null;
  return `#${[m[1], m[2], m[3]].map((n) => Math.min(255, Number(n)).toString(16).padStart(2, '0')).join('')}`;
}

/** A hex colour (#rgb or #rrggbb) at an alpha, as a canvas fill. */
export function rgba(hex: string, a: number): string {
  const h = hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex;
  const n = Number.parseInt(h.slice(1, 7), 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}

/**
 * The scene's colours. `fg`, `muted`, `border` and `brand` are the console's own tokens, read from
 * the page; the five faces of the isometric bodies are the artifact's, one set per theme, because
 * the tokens have no "side of a cabinet" and a face derived from them read as a flat cut-out.
 */
export interface ScenePalette {
  fg: string;
  muted: string;
  border: string;
  brand: string;
  front: string;
  side: string;
  top: string;
  panel: string;
  off: string;
}

export const FACES: Record<'light' | 'dark', Pick<ScenePalette, 'front' | 'side' | 'top' | 'panel' | 'off'>> = {
  light: { front: '#e8eef2', side: '#d3dce2', top: '#fbfcfd', panel: '#dbe3e9', off: '#c9d3da' },
  dark: { front: '#17202a', side: '#0f171f', top: '#243140', panel: '#0c1218', off: '#05080b' },
};

/** What the screen says: the site, and its load once the data is in (null until then, or if none). */
export interface SceneLabel {
  site: string;
  kw: number | null;
}

type Pt = [number, number];
type Ctx = CanvasRenderingContext2D;

const TAU = Math.PI * 2;

function poly(ctx: Ctx, pts: readonly Pt[]): void {
  ctx.beginPath();
  ctx.moveTo(pts[0]?.[0] ?? 0, pts[0]?.[1] ?? 0);
  for (let i = 1; i < pts.length; i++) ctx.lineTo((pts[i] as Pt)[0], (pts[i] as Pt)[1]);
  ctx.closePath();
}
function rr(ctx: Ctx, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  if (typeof ctx.roundRect === 'function') ctx.roundRect(x, y, w, h, r);
  else ctx.rect(x, y, w, h);
}
function line(ctx: Ctx, x1: number, y1: number, x2: number, y2: number): void {
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
}
function glow(ctx: Ctx, x: number, y: number, r: number, hex: string, a: number): void {
  if (a <= 0.01) return;
  ctx.fillStyle = rgba(hex, a * 0.16);
  ctx.beginPath();
  ctx.arc(x, y, r * 3.2, 0, TAU);
  ctx.fill();
  ctx.fillStyle = rgba(hex, a);
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  ctx.fill();
}

/**
 * One frame. `t`: seconds since the loader started. `dn`: seconds since the data arrived, or -1.
 * The caller clears the canvas and sets its scale; this only draws.
 */
export function drawScene(ctx: Ctx, t: number, c: ScenePalette, dn: number, label: SceneLabel): void {
  const k = 19;
  const cs = Math.cos(Math.PI / 6) * k;
  const sn = Math.sin(Math.PI / 6) * k;
  /* The plant sits on a ring around Thunder: power runs round the ring, readings run in along the spokes. */
  const CX = 165;
  const CY = 100;
  const R = 4.6;
  const D2R = Math.PI / 180;
  const A = Math.SQRT2 * R * cs;
  const B = Math.SQRT2 * R * sn;
  const PSI = [160, 113, 67, 20].map((d) => d * D2R) as [number, number, number, number]; /* grid, transformer, UPS, racks */
  let offX = 0;
  let offY = 0;
  const p = (x: number, y: number, z: number): Pt => [CX + (x + offX - y - offY) * cs, CY + (x + offX + y + offY) * sn - z * k];
  /* Place a piece drawn around (ox0, oy0) in its own coordinates at angle psi on the ring. */
  const at = (psi: number, ox0: number, oy0: number): void => {
    const th = psi - Math.PI / 4;
    offX = R * Math.cos(th) - ox0;
    offY = R * Math.sin(th) - oy0;
  };
  const E = (psi: number, r = R): Pt => [CX + Math.SQRT2 * r * cs * Math.cos(psi), CY + Math.SQRT2 * r * sn * Math.sin(psi)];

  const tDone = dn < 0 ? Infinity : t - dn;
  const lvl = ARR.map((a) => clamp01((t - a) / 0.2)) as [number, number, number, number];
  const steady = smooth(INTRO - 0.2, INTRO + 0.5, t);
  const ink = (a: number): string => rgba(c.muted, a);
  const seg = (a: readonly [number, number, number], b: readonly [number, number, number]): void => {
    const P = p(a[0], a[1], a[2]);
    const Q = p(b[0], b[1], b[2]);
    ctx.moveTo(P[0], P[1]);
    ctx.lineTo(Q[0], Q[1]);
  };
  const face = (pts: readonly Pt[], fill: string | CanvasGradient, stroke?: string): void => {
    poly(ctx, pts);
    ctx.fillStyle = fill;
    ctx.fill();
    if (stroke) {
      ctx.strokeStyle = stroke;
      ctx.stroke();
    }
  };
  const lerp = (P: Pt, Q: Pt, s: number): Pt => [P[0] + (Q[0] - P[0]) * s, P[1] + (Q[1] - P[1]) * s];
  const quad = (P: Pt, C: Pt, Q: Pt, s: number): Pt => [
    (1 - s) * (1 - s) * P[0] + 2 * (1 - s) * s * C[0] + s * s * Q[0],
    (1 - s) * (1 - s) * P[1] + 2 * (1 - s) * s * C[1] + s * s * Q[1],
  ];
  const trace = (fn: (s: number) => Pt, s0: number, s1: number, style: string | CanvasGradient, lw: number): void => {
    ctx.strokeStyle = style;
    ctx.lineWidth = lw;
    ctx.beginPath();
    for (let i = 0; i <= 24; i++) {
      const q = fn(s0 + ((s1 - s0) * i) / 24);
      if (i === 0) ctx.moveTo(q[0], q[1]);
      else ctx.lineTo(q[0], q[1]);
    }
    ctx.stroke();
  };
  const box = (x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, lit: number): void => {
    ctx.lineWidth = 1;
    const side: Pt[] = [p(x1, y0, z0), p(x1, y1, z0), p(x1, y1, z1), p(x1, y0, z1)];
    const front: Pt[] = [p(x0, y1, z0), p(x1, y1, z0), p(x1, y1, z1), p(x0, y1, z1)];
    const top: Pt[] = [p(x0, y0, z1), p(x1, y0, z1), p(x1, y1, z1), p(x0, y1, z1)];
    face(side, c.side, c.border);
    face(front, c.front, c.border);
    face(top, c.top, c.border);
    if (lit > 0.01) {
      face(side, rgba(c.brand, 0.07 * lit));
      face(front, rgba(c.brand, 0.1 * lit));
      face(top, rgba(c.brand, 0.06 * lit));
      ctx.strokeStyle = rgba(c.brand, 0.5 * lit);
      for (const f of [side, front, top]) {
        poly(ctx, f);
        ctx.stroke();
      }
    }
  };
  const shadow = (x0: number, y0: number, x1: number, y1: number, a: number): void =>
    face([p(x0, y0, 0), p(x1, y0, 0), p(x1, y1, 0), p(x0, y1, 0)], rgba(c.fg, a));
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  /* ── The floor: the ring (its far half dashed), the spokes, and Thunder's hub at the centre ── */
  const ringFn = (a0: number, a1: number) => (s: number): Pt => E(a0 + (a1 - a0) * s);
  ctx.lineWidth = 1;
  ctx.strokeStyle = rgba(c.border, 0.7);
  ctx.setLineDash([2, 4]);
  ctx.beginPath();
  ctx.ellipse(CX, CY, A, B, 0, Math.PI, TAU);
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(CX, CY, A * 0.5, B * 0.5, 0, 0, TAU);
  ctx.strokeStyle = rgba(c.border, 0.45);
  ctx.stroke();
  ctx.setLineDash([]);
  trace(ringFn(Math.PI, 0), 0, 1, c.border, 1.8);

  /* First load: the pulse goes on past the racks, round the far side, and closes the ring at the
     grid. Drawn here, under the screen, because that half of the ring passes behind it. */
  const back = ringFn(PSI[3] - 13 * D2R, PSI[0] - TAU + 10 * D2R);
  if (t > LOOP[0]) {
    const s = clamp01((t - LOOP[0]) / (LOOP[1] - LOOP[0]));
    ctx.setLineDash([2, 4]);
    trace(back, 0, s, rgba(c.brand, 0.4), 1.2);
    ctx.setLineDash([]);
    if (s < 1) {
      for (let i = 0; i < 9; i++) {
        const s0 = s - 0.12 * (1 - i / 9);
        const s1 = s - 0.12 * (1 - (i + 1) / 9);
        if (s1 <= 0) continue;
        const P = back(Math.max(0, s0));
        const Q = back(s1);
        ctx.strokeStyle = rgba(c.brand, ((i + 1) / 9) * 0.9);
        ctx.lineWidth = 2.2;
        line(ctx, P[0], P[1], Q[0], Q[1]);
      }
      const hd = back(s);
      glow(ctx, hd[0], hd[1], 2.4, c.brand, 1);
    } else {
      /* Once closed, one slow packet keeps orbiting the far side. */
      const so = ((t - LOOP[1]) * 0.28) % 1;
      const q = back(so);
      glow(ctx, q[0], q[1], 1.2, c.brand, 0.7 * Math.sin(Math.PI * so));
    }
  }
  /* The moment it closes, the whole ring flashes once. */
  const closed = t >= LOOP[1] ? Math.exp(-(t - LOOP[1]) * 3.5) : 0;
  if (closed > 0.02) {
    ctx.beginPath();
    ctx.ellipse(CX, CY, A, B, 0, 0, TAU);
    ctx.strokeStyle = rgba(c.brand, 0.55 * closed);
    ctx.lineWidth = 1 + 2 * closed;
    ctx.stroke();
  }

  const hubR = 0.55;
  const hubA = Math.SQRT2 * hubR * cs;
  const hubB = Math.SQRT2 * hubR * sn;
  const spoke = PSI.map((psi) => (s: number): Pt => lerp(E(psi, R - 0.55), E(psi, hubR + 0.05), s));
  ctx.setLineDash([1.5, 3]);
  ctx.lineWidth = 1;
  spoke.forEach((fn, i) => {
    const on = (lvl[i] ?? 0) > 0.5;
    trace(fn, 0, 1, rgba(on ? c.brand : c.border, on ? 0.35 + 0.15 * steady : 0.8), 1);
  });
  ctx.setLineDash([]);

  /* Readings in flight (and just landed). Each runs in along its spoke, then up the beam to the chart. */
  const emits = emitsAt(t, tDone);
  let hubFlash = 0;
  let flash = 0;
  for (const { at: e } of emits) {
    const H = e + FLY * 0.5;
    const L = e + FLY;
    if (t >= H) hubFlash = Math.max(hubFlash, Math.exp(-(t - H) * 6));
    if (t >= L) flash = Math.max(flash, Math.exp(-(t - L) * 7));
  }
  const doneGlow = dn >= 0 ? Math.exp(-dn * 2.2) : 0;
  const hubOn = smooth(LOOP[1], LOOP[1] + 0.35, t);
  const screenOn = smooth(LOOP[1] + 0.3, LOOP[1] + 0.75, t);

  ctx.beginPath();
  ctx.ellipse(CX, CY, hubA * 1.9, hubB * 1.9, 0, 0, TAU);
  ctx.fillStyle = rgba(c.brand, 0.04 + 0.05 * hubOn);
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(CX, CY + 1.5, hubA, hubB, 0, 0, TAU);
  ctx.fillStyle = c.side;
  ctx.fill();
  ctx.beginPath();
  ctx.ellipse(CX, CY, hubA, hubB, 0, 0, TAU);
  ctx.fillStyle = c.top;
  ctx.fill();
  ctx.strokeStyle = c.border;
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(CX, CY, hubA * 0.55, hubB * 0.55, 0, 0, TAU);
  ctx.fillStyle = c.panel;
  ctx.fill();
  const hubLit = hubOn * (0.45 + 0.3 * Math.sin(t * 3)) + hubFlash * 0.6 + doneGlow;
  ctx.beginPath();
  ctx.ellipse(CX, CY, hubA, hubB, 0, 0, TAU);
  ctx.strokeStyle = rgba(c.brand, clamp01(hubLit));
  ctx.lineWidth = 1.2;
  ctx.stroke();
  if (hubFlash > 0.02) {
    ctx.beginPath();
    ctx.ellipse(CX, CY, hubA * (1 + (1 - hubFlash) * 1.2), hubB * (1 + (1 - hubFlash) * 1.2), 0, 0, TAU);
    ctx.strokeStyle = rgba(c.brand, 0.5 * hubFlash);
    ctx.lineWidth = 1;
    ctx.stroke();
  }

  /* ── The Thunder screen, floating over its hub on a beam of light ── */
  const bob = Math.sin((TAU * t) / 3.6) * 1.2;
  const X0 = CX - 56;
  const SW = 112;
  const Y0 = 8 + bob;
  const SH = 62;
  const TH = 5;
  const sx0 = X0 + 4;
  const sy0 = Y0 + 4;
  const sw = SW - 8;
  const sh = SH - 8;
  const bg = ctx.createLinearGradient(0, CY, 0, Y0 + SH);
  bg.addColorStop(0, rgba(c.brand, 0.28 * hubOn));
  bg.addColorStop(1, rgba(c.brand, 0.04 * hubOn));
  ctx.fillStyle = bg;
  ctx.beginPath();
  ctx.moveTo(CX - hubA * 0.55, CY);
  ctx.lineTo(CX - 5, Y0 + SH);
  ctx.lineTo(CX + 5, Y0 + SH);
  ctx.lineTo(CX + hubA * 0.55, CY);
  ctx.closePath();
  ctx.fill();

  ctx.lineWidth = 1;
  rr(ctx, X0 + 3, Y0 - TH, SW - 6, TH + 3, 2);
  ctx.fillStyle = c.top;
  ctx.fill();
  ctx.strokeStyle = c.border;
  ctx.stroke();
  rr(ctx, X0, Y0, SW, SH, 4);
  ctx.fillStyle = c.front;
  ctx.fill();
  ctx.strokeStyle = c.border;
  ctx.stroke();
  const edge = Math.max(flash * 0.55, doneGlow * 0.9);
  if (edge > 0.01) {
    rr(ctx, X0, Y0, SW, SH, 4);
    ctx.strokeStyle = rgba(c.brand, edge);
    ctx.lineWidth = 1.4;
    ctx.stroke();
    ctx.lineWidth = 1;
  }
  rr(ctx, sx0, sy0, sw, sh, 2.5);
  ctx.fillStyle = c.panel;
  ctx.fill();

  /* The wordmark, the site the header has selected (cut to the room it has), and a live dot. */
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  ctx.font = '700 6.5px Inter, system-ui, sans-serif';
  ctx.fillStyle = rgba(c.fg, 0.88);
  let wx = sx0 + 6;
  for (const ch of 'THUNDER') {
    ctx.fillText(ch, wx, sy0 + 10);
    wx += ctx.measureText(ch).width + 1.3;
  }
  ctx.font = '500 6px "Geist Mono", ui-monospace, monospace';
  ctx.fillStyle = c.muted;
  const dotX = sx0 + sw - 8;
  ctx.fillText(truncateToWidth(label.site, dotX - 5 - (wx + 4), (s) => ctx.measureText(s).width), wx + 4, sy0 + 10);
  glow(ctx, dotX, sy0 + 7.5, 1.6, c.brand, dn >= 0 ? 1 : 0.35 + 0.45 * (0.5 + 0.5 * Math.sin(t * 5)));

  /* The site load, only once the site's own reading is in; until then the slot says it is reading. */
  const figY = sy0 + 23.5;
  if (dn >= 0 && label.kw !== null) {
    const fig = String(Math.round(label.kw));
    ctx.font = '600 12px "Geist Mono", ui-monospace, monospace';
    ctx.fillStyle = c.fg;
    ctx.fillText(fig, sx0 + 6, figY);
    const wv = ctx.measureText(fig).width;
    const snap = Math.exp(-dn * 4);
    if (snap > 0.02) glow(ctx, sx0 + 6 + wv / 2, figY - 4, 5, c.brand, 0.25 * snap);
    ctx.font = '500 7px Inter, system-ui, sans-serif';
    ctx.fillStyle = c.muted;
    ctx.fillText('kW', sx0 + 6 + wv + 2.5, figY);
  }
  ctx.font = '500 6.5px Inter, system-ui, sans-serif';
  ctx.textAlign = 'right';
  ctx.fillStyle = rgba(c.muted, 0.9);
  ctx.fillText(dn >= 0 ? 'site load' : 'reading…', sx0 + sw - 6, figY);
  ctx.textAlign = 'left';

  /* The chart: a line drawing itself as readings land. Its shape is motion, not data — no axis, no figure. */
  const val = (n: number): number => 0.28 + 0.52 * (0.55 * seeded(n * 3 + 1) + 0.45 * (0.5 + 0.5 * Math.sin(n * 0.9)));
  const cx0 = sx0 + 6;
  const cx1 = sx0 + sw - 6;
  const cy0 = sy0 + 29;
  const cy1 = sy0 + sh - 5;
  const NP = 7;
  const dx = (cx1 - cx0) / (NP - 1);
  const vy = (n: number): number => cy1 - val(n) * (cy1 - cy0);
  ctx.lineWidth = 1;
  ctx.strokeStyle = rgba(c.border, 0.9);
  line(ctx, cx0, cy1 + 0.5, cx1, cy1 + 0.5);
  ctx.setLineDash([1.5, 2.5]);
  ctx.strokeStyle = rgba(c.border, 0.8);
  line(ctx, cx0, cy0, cx1, cy0);
  line(ctx, cx0, (cy0 + cy1) / 2, cx1, (cy0 + cy1) / 2);
  ctx.setLineDash([]);
  const landT = Math.min(t, tDone + FLY);
  const raw = (landT - EMIT0 - FLY) / EMIT_EVERY;
  const ptX = (n: number): number => cx0 + Math.min(n, NP - 1) * dx;
  if (raw >= 0) {
    const base = Math.floor(raw);
    const prog = base + smooth(0, 0.4, raw - base);
    const off = Math.max(0, prog - (NP - 1));
    const X = (j: number): number => cx0 + (j - off) * dx;
    const pts: Pt[] = [];
    for (let j = Math.max(0, Math.floor(off)); j <= Math.floor(prog); j++) pts.push([X(j), vy(j)]);
    const f = prog - Math.floor(prog);
    if (f > 0.001) pts.push([X(prog), vy(Math.floor(prog)) + (vy(Math.floor(prog) + 1) - vy(Math.floor(prog))) * f]);
    ctx.save();
    ctx.beginPath();
    ctx.rect(cx0, cy0 - 4, cx1 - cx0, cy1 - cy0 + 6);
    ctx.clip();
    if (pts.length > 1) {
      const end = pts[pts.length - 1] as Pt;
      ctx.beginPath();
      ctx.moveTo((pts[0] as Pt)[0], cy1);
      for (const q of pts) ctx.lineTo(q[0], q[1]);
      ctx.lineTo(end[0], cy1);
      ctx.closePath();
      const ga = ctx.createLinearGradient(0, cy0, 0, cy1);
      ga.addColorStop(0, rgba(c.brand, 0.22));
      ga.addColorStop(1, rgba(c.brand, 0));
      ctx.fillStyle = ga;
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo((pts[0] as Pt)[0], (pts[0] as Pt)[1]);
      for (const q of pts.slice(1)) ctx.lineTo(q[0], q[1]);
      ctx.strokeStyle = c.brand;
      ctx.lineWidth = 1.4;
      ctx.stroke();
    }
    ctx.restore();
    const end = pts[pts.length - 1];
    if (end) glow(ctx, end[0], end[1], 1.6, c.brand, 1);
  }
  for (const { at: e, n } of emits) {
    const L = e + FLY;
    const fr = t >= L ? Math.exp(-(t - L) * 6) : 0;
    if (fr < 0.02) continue;
    ctx.strokeStyle = rgba(c.brand, 0.5 * fr);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(ptX(n), vy(n), 2 + (1 - fr) * 7, 0, TAU);
    ctx.stroke();
  }

  /* Until the ring closes the screen is dark; then a line sweeps down and it comes on. */
  if (screenOn < 0.999) {
    rr(ctx, sx0, sy0, sw, sh, 2.5);
    ctx.fillStyle = rgba(c.off, 0.92 * (1 - screenOn));
    ctx.fill();
    if (screenOn > 0.01) {
      const ly = sy0 + sh * screenOn;
      ctx.strokeStyle = rgba(c.brand, 0.8 * (1 - screenOn));
      ctx.lineWidth = 1.2;
      line(ctx, sx0 + 2, ly, sx0 + sw - 2, ly);
    }
  }

  const SRC: Pt[] = [];

  /* ── The grid: a lattice pylon at the ring's left end ── */
  at(PSI[0], 0, 0);
  shadow(-0.62, -0.62, 0.62, 0.62, 0.035);
  const PW = (z: number): number => 0.42 - 0.31 * Math.min(1, z / 3.4);
  const LV = [0, 0.95, 1.85, 2.65, 3.4];
  ctx.beginPath();
  seg([-PW(0), -PW(0), 0], [-PW(3.4), -PW(3.4), 3.4]);
  for (let i = 0; i < LV.length - 1; i++) {
    const z1 = LV[i] as number;
    const z2 = LV[i + 1] as number;
    const w1 = PW(z1);
    const w2 = PW(z2);
    seg([-w1, -w1, z1], [w2, -w2, z2]);
    seg([w1, -w1, z1], [-w2, -w2, z2]);
    seg([-w1, -w1, z1], [-w2, w2, z2]);
    seg([-w1, w1, z1], [-w2, -w2, z2]);
  }
  for (const z of LV.slice(1)) {
    const w = PW(z);
    seg([-w, -w, z], [w, -w, z]);
    seg([-w, -w, z], [-w, w, z]);
  }
  ctx.lineWidth = 0.8;
  ctx.strokeStyle = ink(0.26);
  ctx.stroke();
  const lattice = (): void => {
    ctx.beginPath();
    for (const [sx, sy] of [[-1, 1], [1, 1], [1, -1]] as const) seg([sx * PW(0), sy * PW(0), 0], [sx * PW(3.4), sy * PW(3.4), 3.4]);
    for (const z of LV.slice(1)) {
      const w = PW(z);
      seg([-w, w, z], [w, w, z]);
      seg([w, w, z], [w, -w, z]);
    }
    for (let i = 0; i < LV.length - 1; i++) {
      const z1 = LV[i] as number;
      const z2 = LV[i + 1] as number;
      const w1 = PW(z1);
      const w2 = PW(z2);
      seg([-w1, w1, z1], [w2, w2, z2]);
      seg([w1, w1, z1], [-w2, w2, z2]);
      seg([w1, w1, z1], [w2, -w2, z2]);
      seg([w1, -w1, z1], [w2, w2, z2]);
    }
    const w = PW(3.4);
    for (const [sx, sy] of [[-1, 1], [1, 1], [1, -1]] as const) seg([sx * w, sy * w, 3.4], [0, 0, 3.92]);
    seg([-1.15, 0, 3.22], [1.15, 0, 3.22]);
    seg([-1.15, 0, 3.22], [-PW(2.95), 0, 2.95]);
    seg([1.15, 0, 3.22], [PW(2.95), 0, 2.95]);
    seg([-0.85, 0, 2.5], [0.85, 0, 2.5]);
    seg([-0.85, 0, 2.5], [-PW(2.25), 0, 2.25]);
    seg([0.85, 0, 2.5], [PW(2.25), 0, 2.25]);
  };
  lattice();
  ctx.lineWidth = 0.9;
  ctx.strokeStyle = ink(0.8);
  ctx.stroke();
  if (lvl[0] > 0.01) {
    lattice();
    ctx.lineWidth = 1.05;
    ctx.strokeStyle = rgba(c.brand, 0.85 * lvl[0]);
    ctx.stroke();
  }
  const insulator = (x: number, z: number): Pt => {
    const a = p(x, 0, z);
    const b = p(x, 0, z - 0.32);
    ctx.strokeStyle = ink(0.85);
    ctx.lineWidth = 1;
    line(ctx, a[0], a[1], b[0], b[1]);
    for (let s = 1; s <= 3; s++) {
      const yy = a[1] + ((b[1] - a[1]) * s) / 3.6;
      ctx.beginPath();
      ctx.ellipse(a[0], yy, 2.1, 0.9, 0, 0, TAU);
      ctx.fillStyle = c.top;
      ctx.fill();
      ctx.strokeStyle = ink(0.75);
      ctx.stroke();
    }
    return b;
  };
  const Wul = insulator(-1.15, 3.22);
  const Wll = insulator(-0.85, 2.5);
  const Wur = insulator(1.15, 3.22);
  const Wlr = insulator(0.85, 2.5);
  const incoming = [Wul, Wll].map((P) => {
    const Q: Pt = [P[0] - 40, P[1] + 3];
    const C: Pt = [P[0] - 20, P[1] + 8];
    const g = ctx.createLinearGradient(P[0], 0, Q[0], 0);
    g.addColorStop(0, ink(0.6));
    g.addColorStop(1, ink(0));
    const fn = (s: number): Pt => quad(Q, C, P, s);
    trace(fn, 0, 1, g, 1);
    return fn;
  });
  if (lvl[0] > 0.01) for (const W of [Wur, Wlr, Wul, Wll]) glow(ctx, W[0], W[1], 1.15, c.brand, lvl[0]);
  SRC[0] = p(0, 0, 3.92);

  /* ── The racks at the ring's right end, a scan rising through the doors every 2.6 s ── */
  at(PSI[3], 6.345, -5.325);
  ctx.lineWidth = 1;
  ctx.strokeStyle = rgba(c.border, 0.45);
  ctx.beginPath();
  for (let i = 0; i <= 6; i++) seg([4.85 + i * 0.5, -5.9, 0], [4.85 + i * 0.5, -4.7, 0]);
  for (let i = 0; i <= 3; i++) seg([4.85, -5.9 + i * 0.4, 0], [7.85, -5.9 + i * 0.4, 0]);
  ctx.stroke();
  const HZ = 2.2;
  const local = t - ARR[3];
  const scanT = local >= 0 ? local % 2.6 : -1;
  const zScan = scanT >= 0 && scanT < 0.8 ? -0.4 + (scanT / 0.8) * (HZ + 0.8) : -9;
  for (let r = 0; r < 3; r++) {
    const x0 = 5.0 + r * 0.92;
    const x1 = x0 + 0.85;
    const y0 = -5.75;
    const y1 = -4.9;
    box(x0, y0, 0, x1, y1, HZ, lvl[3] * 0.5);
    const a0 = x0 + 0.07;
    const a1 = x1 - 0.07;
    const zb = 0.09;
    const zt = HZ - 0.1;
    face([p(a0, y1, zb), p(a1, y1, zb), p(a1, y1, zt), p(a0, y1, zt)], c.panel);
    const za = Math.max(zb, zScan - 0.4);
    const zc = Math.min(zt, zScan + 0.4);
    if (zc > za) {
      const g0 = p(a0, y1, zScan - 0.4);
      const g1 = p(a0, y1, zScan + 0.4);
      const g = ctx.createLinearGradient(g0[0], g0[1], g1[0], g1[1]);
      g.addColorStop(0, rgba(c.brand, 0));
      g.addColorStop(0.5, rgba(c.brand, 0.32));
      g.addColorStop(1, rgba(c.brand, 0));
      face([p(a0, y1, za), p(a1, y1, za), p(a1, y1, zc), p(a0, y1, zc)], g);
    }
    for (let u = 0; u < 7; u++) {
      const z = 0.17 + u * 0.27;
      const l0 = p(a0 + 0.03, y1, z);
      const l1 = p(a1 - 0.03, y1, z);
      ctx.lineWidth = 1;
      ctx.strokeStyle = rgba(c.border, 0.7);
      line(ctx, l0[0], l0[1], l1[0], l1[1]);
      const on = clamp01((t - (ARR[3] + r * 0.07 + u * 0.035)) / 0.12);
      for (let j = 0; j < 2; j++) {
        const s = seeded(r * 100 + u * 10 + j + 300);
        const zl = z + 0.13;
        const hit = clamp01(1 - Math.abs(zScan - zl) / 0.42);
        const tw = 0.5 + 0.5 * Math.sin(t * (1.4 + s * 4.2) + s * 20);
        const b = clamp01(on * (s > 0.38 ? 0.45 + 0.45 * tw : 0.22) + hit);
        const q = p(a0 + 0.12 + j * 0.1, y1, zl);
        if (b > 0.1) glow(ctx, q[0], q[1], 1.15, c.brand, b);
        else {
          ctx.fillStyle = c.border;
          ctx.beginPath();
          ctx.arc(q[0], q[1], 1, 0, TAU);
          ctx.fill();
        }
      }
      const b0 = p(a0 + 0.38, y1, z + 0.13);
      const b1 = p(a1 - 0.07, y1, z + 0.13);
      ctx.strokeStyle = rgba(c.muted, 0.35);
      line(ctx, b0[0], b0[1], b1[0], b1[1]);
    }
  }
  SRC[3] = p(6.345, -5.325, HZ);

  /* ── The transformer, front left of the ring ── */
  at(PSI[1], 2.1, -2.1);
  shadow(1.4, -2.64, 2.95, -1.45, 0.045);
  box(1.48, -2.57, 0, 2.72, -1.63, 0.08, 0);
  box(1.55, -2.5, 0.08, 2.65, -1.7, 1.12, lvl[1]);
  ctx.lineWidth = 1;
  for (let j = 0; j < 4; j++) {
    const yf = -2.4 + j * 0.2;
    const fin: Pt[] = [p(2.65, yf, 0.22), p(2.84, yf, 0.22), p(2.84, yf, 0.96), p(2.65, yf, 0.96)];
    face(fin, c.front, c.border);
    if (lvl[1] > 0.01) face(fin, rgba(c.brand, 0.12 * lvl[1]));
  }
  for (let i = 0; i < 6; i++) {
    const xf = 1.66 + i * 0.165;
    const fin: Pt[] = [p(xf, -1.7, 0.22), p(xf, -1.51, 0.22), p(xf, -1.51, 0.96), p(xf, -1.7, 0.96)];
    face(fin, c.side, c.border);
    if (lvl[1] > 0.01) face(fin, rgba(c.brand, 0.12 * lvl[1]));
  }
  const bush = [1.74, 2.1, 2.46].map((x) => {
    const a = p(x, -2.1, 1.12);
    const b = p(x, -2.1, 1.62);
    ctx.strokeStyle = ink(0.85);
    ctx.lineWidth = 1.5;
    line(ctx, a[0], a[1], b[0], b[1]);
    ctx.lineWidth = 0.9;
    for (let s = 0; s < 3; s++) {
      const yy = a[1] + ((b[1] - a[1]) * (s + 0.6)) / 3.4;
      ctx.beginPath();
      ctx.ellipse(a[0], yy, 2.1, 0.9, 0, 0, TAU);
      ctx.fillStyle = c.top;
      ctx.fill();
      ctx.strokeStyle = ink(0.7);
      ctx.stroke();
    }
    ctx.fillStyle = ink(0.9);
    ctx.beginPath();
    ctx.arc(b[0], b[1], 1.1, 0, TAU);
    ctx.fill();
    return b;
  }) as [Pt, Pt, Pt];
  bush.forEach((b) => glow(ctx, b[0], b[1], 1.3, c.brand, lvl[1]));
  SRC[1] = bush[1];

  /* ── The UPS, front right of the ring ── */
  at(PSI[2], 3.9, -3.9);
  shadow(3.42, -4.4, 4.44, -3.38, 0.045);
  box(3.5, -4.3, 0, 4.3, -3.5, 1.85, lvl[2]);
  ctx.lineWidth = 1;
  face([p(3.6, -3.5, 1.36), p(4.02, -3.5, 1.36), p(4.02, -3.5, 1.65), p(3.6, -3.5, 1.65)], c.panel, c.border);
  const charge = (t * 1.6) % 5;
  for (let b = 0; b < 4; b++) {
    const x0 = 3.65 + b * 0.088;
    const intro = clamp01((t - ARR[2] - b * 0.08) / 0.1);
    const cyc = charge > b ? 1 : 0.35;
    const on = intro * (1 - steady + steady * cyc);
    face(
      [p(x0, -3.5, 1.43), p(x0 + 0.058, -3.5, 1.43), p(x0 + 0.058, -3.5, 1.58), p(x0, -3.5, 1.58)],
      on > 0.02 ? rgba(c.brand, 0.25 + 0.7 * on) : rgba(c.border, 0.95),
    );
  }
  const led = p(4.17, -3.5, 1.5);
  if (lvl[2] > 0.05) glow(ctx, led[0], led[1], 1.4, c.brand, lvl[2] * (0.75 + 0.25 * Math.sin(t * 5)));
  else {
    ctx.fillStyle = c.border;
    ctx.beginPath();
    ctx.arc(led[0], led[1], 1.3, 0, TAU);
    ctx.fill();
  }
  ctx.strokeStyle = ink(0.35);
  ctx.beginPath();
  for (const z of [0.3, 0.45, 0.6, 0.75]) seg([3.6, -3.5, z], [4.2, -3.5, z]);
  for (const z of [0.4, 0.55, 0.7]) seg([4.3, -4.2, z], [4.3, -3.75, z]);
  ctx.stroke();
  SRC[2] = p(3.9, -3.9, 1.85);

  /* ── The power path: overhead from the pylon to the bushings, then round the ring ── */
  const sagC = (P: Pt, Q: Pt): Pt => [(P[0] + Q[0]) / 2 - 4, Math.max(P[1], Q[1]) + 4];
  const wire1 = (s: number): Pt => quad(Wur, sagC(Wur, bush[0]), bush[0], s);
  const wire2 = (s: number): Pt => quad(Wlr, sagC(Wlr, bush[1]), bush[1], s);
  trace(wire1, 0, 1, ink(0.65), 1);
  trace(wire2, 0, 1, ink(0.65), 1);
  const arcA = ringFn(PSI[1] - 9 * D2R, PSI[2] + 9 * D2R);
  const arcB = ringFn(PSI[2] - 9 * D2R, PSI[3] + 13 * D2R);

  const legs: Array<[(s: number) => Pt, number, number]> = [
    [wire1, 0.08, 0.56],
    [arcA, 0.66, 0.92],
    [arcB, 1.02, 1.28],
  ];
  for (const [fn, a, b] of legs) {
    if (t <= a) continue;
    const s = clamp01((t - a) / (b - a));
    trace(fn, 0, s, rgba(c.brand, 0.55), 1.6);
    if (s < 1) {
      const N = 9;
      for (let i = 0; i < N; i++) {
        const s0 = s - 0.3 * (1 - i / N);
        const s1 = s - 0.3 * (1 - (i + 1) / N);
        if (s1 <= 0) continue;
        const P = fn(Math.max(0, s0));
        const Q = fn(s1);
        ctx.strokeStyle = rgba(c.brand, ((i + 1) / N) * 0.95);
        ctx.lineWidth = 2.4;
        line(ctx, P[0], P[1], Q[0], Q[1]);
      }
      const hd = fn(s);
      glow(ctx, hd[0], hd[1], 2.5, c.brand, 1);
    }
  }
  if (lvl[1] > 0.01) trace(wire2, 0, 1, rgba(c.brand, 0.4 * lvl[1]), 1.3);

  if (steady > 0.01) {
    const flows: Array<[(s: number) => Pt, number, number]> = [
      [incoming[0] as (s: number) => Pt, 0.0, 0.55],
      [incoming[1] as (s: number) => Pt, 0.4, 0.55],
      [wire1, 0.15, 0.7],
      [wire2, 0.6, 0.7],
      [arcA, 0.3, 0.8],
      [arcB, 0.75, 0.8],
    ];
    for (const [fn, off, speed] of flows) {
      for (let i = 0; i < 2; i++) {
        const s = (t * speed + off + i * 0.5) % 1;
        const a = Math.sin(Math.PI * s) * steady;
        const tail = fn(Math.max(0, s - 0.1));
        const hd = fn(s);
        const g = ctx.createLinearGradient(tail[0], tail[1], hd[0], hd[1]);
        g.addColorStop(0, rgba(c.brand, 0));
        g.addColorStop(1, rgba(c.brand, 0.8 * a));
        ctx.strokeStyle = g;
        ctx.lineWidth = 1.8;
        line(ctx, tail[0], tail[1], hd[0], hd[1]);
        glow(ctx, hd[0], hd[1], 1.3, c.brand, a);
      }
    }
  }

  /* ── Readings: a ring where it leaves, in along the spoke to the hub, then up the beam to the chart ── */
  for (const { at: e, src, n } of emits) {
    const S = SRC[src] as Pt;
    const u0 = (t - e) / 0.42;
    if (u0 > 0 && u0 < 1) {
      ctx.strokeStyle = rgba(c.brand, (1 - u0) * 0.45);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(S[0], S[1], 3 + u0 * 9, 0, TAU);
      ctx.stroke();
    }
    const u = (t - e) / FLY;
    if (u <= 0 || u >= 1) continue;
    const T: Pt = [ptX(n), vy(n)];
    const toHub = spoke[src] as (s: number) => Pt;
    const path = (v: number): Pt => (v < 0.5 ? toHub(easeIO(v / 0.5)) : quad([CX, CY], [CX, Y0 + SH + 2], T, easeIO((v - 0.5) / 0.5)));
    for (let i = 3; i >= 1; i--) {
      const q = path(Math.max(0, u - i * 0.03));
      ctx.fillStyle = rgba(c.brand, 0.18 * (4 - i));
      ctx.beginPath();
      ctx.arc(q[0], q[1], 1.1, 0, TAU);
      ctx.fill();
    }
    const q = path(u);
    glow(ctx, q[0], q[1], 1.6, c.brand, 1);
  }
}
